import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import AdminShell from "../components/AdminShell";
import { getAdminReportsNewUsers, isApiConfigured } from "../apiClient";

const RANGE_OPTIONS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "180", label: "Last 180 days" },
];

function formatDay(isoDate) {
  if (!isoDate) return "—";
  const d = new Date(`${String(isoDate).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatRange(from, to) {
  if (!from || !to) return "";
  if (from === to) return formatDay(from);
  return `${formatDay(from)} – ${formatDay(to)}`;
}

export default function AdminNewUsersReportPage({ currentUser, authToken, onSignOut }) {
  const [days, setDays] = useState("30");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadReport = useCallback(async () => {
    const t = String(authToken || "").trim();
    if (!t || !isApiConfigured()) {
      setError("Not signed in.");
      setReport(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await getAdminReportsNewUsers(t, { days: Number(days) });
      setReport(data?.report || null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load this report.");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [authToken, days]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  const rows = useMemo(() => {
    const list = Array.isArray(report?.days) ? report.days : [];
    return [...list].reverse();
  }, [report]);

  if (currentUser?.role !== "admin") {
    return <Navigate to="/admin/signin" replace />;
  }

  const totals = report?.totals || { total: 0, hiringManagers: 0, applicants: 0 };

  return (
    <AdminShell currentUser={currentUser} onSignOut={onSignOut}>
      <Link to="/admin/reports" className="jw-admin__back">
        ← Back to reports
      </Link>
      <h1 className="jw-admin__title">New users</h1>
      <p className="jw-admin__lede">
        Product sign-ups per day (UTC). {report ? formatRange(report.from, report.to) : null}
      </p>

      <div className="jw-admin-report-toolbar">
        <label className="jw-admin-report-toolbar__label" htmlFor="admin-new-users-days">
          Range
        </label>
        <select
          id="admin-new-users-days"
          className="jw-admin-report-toolbar__select"
          value={days}
          onChange={(event) => setDays(event.target.value)}
          disabled={loading}
        >
          {RANGE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <button type="button" className="jw-admin__refresh" onClick={() => void loadReport()} disabled={loading}>
          Refresh
        </button>
      </div>

      {error ? (
        <p className="jw-admin__error" role="alert">
          {error}
        </p>
      ) : null}

      {loading && !report ? <p className="jw-admin__muted">Loading report…</p> : null}

      {report ? (
        <>
          <div className="jw-admin-report-stats" aria-label="Totals for selected range">
            <div className="jw-admin-report-stat">
              <span className="jw-admin-report-stat__value">{totals.total}</span>
              <span className="jw-admin-report-stat__label">Total sign-ups</span>
            </div>
            <div className="jw-admin-report-stat">
              <span className="jw-admin-report-stat__value">{totals.hiringManagers}</span>
              <span className="jw-admin-report-stat__label">Hiring managers</span>
            </div>
            <div className="jw-admin-report-stat">
              <span className="jw-admin-report-stat__value">{totals.applicants}</span>
              <span className="jw-admin-report-stat__label">Applicants</span>
            </div>
          </div>

          <div className="jw-admin-rates__table-wrap">
            <table className="jw-admin-rates__table jw-admin-report-table">
              <thead>
                <tr>
                  <th scope="col">Day (UTC)</th>
                  <th scope="col" className="jw-admin-report-table__num">
                    Total
                  </th>
                  <th scope="col" className="jw-admin-report-table__num">
                    Hiring managers
                  </th>
                  <th scope="col" className="jw-admin-report-table__num">
                    Applicants
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="jw-admin__muted">
                      No sign-ups in this range.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.day}>
                      <td>{formatDay(row.day)}</td>
                      <td className="jw-admin-report-table__num">{row.total}</td>
                      <td className="jw-admin-report-table__num">{row.hiringManagers}</td>
                      <td className="jw-admin-report-table__num">{row.applicants}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </AdminShell>
  );
}
